import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { arr } from '@/features/shared/lib/utils';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import AuthorsTable from '@/features/publication/components/AuthorsTable';
import ReferencesTable from '@/features/publication/components/ReferencesTable';
import AuthorOtherPapers from '@/features/publication/components/AuthorOtherPapers';
import AuthorsByCountryDonut from '@/features/publication/components/charts/AuthorsByCountryDonut';
import AuthorsByInstitutionDonut from '@/features/publication/components/charts/AuthorsByInstitutionDonut';
import CitingPapersList from '@/features/publication/components/CitingPapersList';
import { useMetricsWidgets } from '@/features/shared/lib/metrics-widgets';

export default function Publication() {
  const { id: pid } = useParams();
  const [sp] = useSearchParams();
  const id = pid || sp.get('id');
  const altmetricRef = useRef(null);

  const navigate = useNavigate();
  const handleBack = (e) => {
    try { if (e && typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) { }
    try {
      if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
        window.history.back();
      } else {
        navigate('/publications');
      }
    } catch (err) {
      navigate('/publications');
    }
  };
  const handlePrint = (e) => {
    try { if (e && typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) { }
    try { if (typeof window !== 'undefined' && typeof window.print === 'function') window.print(); } catch (err) { }
  };

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [fwciPopupOpen, setFwciPopupOpen] = useState(false);

  // Authors table state and derived lists (hooks must be declared before any early returns)
  const [authorView, setAuthorView] = useState('list'); // 'list' | 'pnu' | 'inst'
  const [activeTab, setActiveTab] = useState('');

  const authorsFlat = useMemo(() => {
    const a = record?.author;
    const out = [];
    try {
      // Case 1: author가 배열인 경우 (새로운 구조) - 그대로 전달
      if (Array.isArray(a)) {
        return a.map((it) => {
          // const author_name = it?.scholar_name_eng || it?.author_name || it?.author_full_name || it?.author_abbr_name || '';
          const primaryName = it?.scholar_name_eng || it?.author_name || it?.author_full_name || it?.author_abbr_name || '';
          const author_name = (primaryName && it?.author_name && primaryName !== it.author_name)
            ? `${primaryName}(${it.author_name})`
            : primaryName;

          const institution = it?.authority_org_name || it?.authority_org_name_kor || it?.org_name || '';
          const col = it?.col_name || '';
          const dept = it?.dept_name || '';
          const affiliation = [col, dept].filter(Boolean).join(' ');
          return {
            ...it, // 원본 데이터의 모든 필드 보존
            author_name,
            institution,
            affiliation,
          };
        });
      }
      // Case 2: author가 객체인 경우 (기존 구조) - 하위 호환성 유지
      if (a && typeof a === 'object') {
        Object.entries(a).forEach(([instName, arrList]) => {
          if (Array.isArray(arrList)) {
            arrList.forEach((it) => {
              const author_name = it?.author_name || it?.author_full_name || it?.author_abbr_name || '';
              const institution = instName || it?.org_name || '';
              const col = it?.col_name || '';
              const dept = it?.dept_name || '';
              const affiliation = [col, dept].filter(Boolean).join(' ');
              const author_role = it?.author_role || '';
              out.push({
                author_id: it?.author_id,
                author_name,
                institution,
                affiliation,
                author_role,
              });
            });
          }
        });
      }
    } catch (e) { }
    return out;
  }, [record]);

  const isPnuInstitution = (name = '') => {
    const n = String(name || '').toLowerCase();
    return n.includes('부산대학교') || n.includes('pusan national university') || n.includes('pnu');
  };

  const filteredAuthors = useMemo(() => {
    if (!authorsFlat.length) return [];
    if (authorView === 'pnu') return authorsFlat.filter((r) => isPnuInstitution(r.institution));
    if (authorView === 'inst') return authorsFlat.filter((r) => !isPnuInstitution(r.institution));
    return authorsFlat;
  }, [authorsFlat, authorView]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await get(`/rm/v1/publication/${encodeURIComponent(id)}`);
        if (!alive) return;
        const rec = data?.result?.record || data?.result || null;
        setRecord(rec);

        // Set default active tab for category ranking
        const cr = arr(rec?.category_ranking);
        if (cr && cr.length > 0) {
          const orderMap = { jcr: 0, scopus: 1, kci: 2 };
          const types = Array.from(new Set(cr.map(r => String(r?.indicator_type || '').toLowerCase()).filter(Boolean)));
          types.sort((a, b) => (orderMap[a] ?? 99) - (orderMap[b] ?? 99));
          if (types.length > 0) {
            setActiveTab(types[0]);
          }
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    const t = record?.title || record?.resource_title;
    if (!t) return;
    const site =
      typeof rmVars !== 'undefined' && rmVars.siteName
        ? String(rmVars.siteName).trim()
        : '';
    document.title = site ? `${t} - Publication - ${site}` : t;
  }, [record?.title, record?.resource_title]);

  // Altmetric/PlumX 위젯 스크립트 로드/초기화 (공통 훅)
  useMetricsWidgets({
    enabled: !loading && !!record,
    delaysMs: { altmetric: 1000, plumx: 1000 },
    cleanupBeforeLoad: true,
    plumxRemoveRenderedDom: true,
    plumxRenderedSelector: '.plx-wrapping-print-link',
    plumxWireUpDelayMs: 150,
  });

  // Move hook declarations above any early returns to keep hooks order stable
  const scholarIds = useMemo(() => {
    try {
      const a = record?.author;
      // Case 1: author is an object keyed by institution name
      if (a && typeof a === 'object' && !Array.isArray(a)) {
        const ids = [];
        Object.entries(a).forEach(([instName, arrList]) => {
          if (!isPnuInstitution(instName)) return;
          if (Array.isArray(arrList)) {
            arrList.forEach((it) => {
              const v = it?.scholar_id;
              if (v != null && v !== '' && !Number.isNaN(v)) ids.push(v);
            });
          }
        });
        return Array.from(new Set(ids));
      }
      // Case 2: author is an array of author objects
      if (Array.isArray(a)) {
        const ids = a
          .filter((it) => isPnuInstitution(it?.org_name || it?.org || it?.institution))
          .map((it) => it?.scholar_id)
          .filter((v) => v != null && v !== '' && !Number.isNaN(v));
        return Array.from(new Set(ids));
      }
    } catch (e) { }
    return [];
  }, [record, isPnuInstitution]);

  const collaborationLabel = useMemo(() => {
    if (!authorsFlat.length) return 'International';

    // 1. 연구자가 1명인 경우
    if (authorsFlat.length === 1) return 'Single';

    // 2. 모든 저자가 부산대 소속인 경우
    const allPnu = authorsFlat.every(a => isPnuInstitution(a.institution || a.org_name || a.org));
    if (allPnu) return 'Only Institutional';

    // 3. 모든 저자가 대한민국 소속인 경우
    const allKorea = authorsFlat.every(a => {
      const country = String(a.country || '').toLowerCase();
      return country === 'south korea' || country === 'korea' || country === 'republic of korea';
    });
    if (allKorea) return 'Only National';

    // 4. 그 외
    return 'International';
  }, [authorsFlat, isPnuInstitution]);

  if (!id) return <div className="p-6">잘못된 접근입니다.</div>;
  if (err) return <div className="p-6 text-red-600">에러: {String(err)}</div>;
  if (loading && !record) return <div className="p-6">Loading...</div>;

  const title = record?.title || record?.resource_title || '';
  const meta = record?.meta || {};
  const abstract = record?.abstract || record?.description || '';
  const keywords = record?.keywords || [];
  const pages = record?.pages || (record?.page_start && record?.page_end ? `${record.page_start}-${record.page_end}` : '');

  return (
    <div className="journaldetailPage detailPage flexPage mt-[127px] lg:mt-[120px] px-6 2xl:px-[100px]" >
      <div className="journal-detail-cont w-full pt-[24px] lg:relative lg:w-[calc(100%-360px)] lg:mt-[105px] lg:bg-white lg:rounded-xl lg:px-[60px] lg:py-[60px] lg:pt-[60px]" >
        <a href="#" onClick={handleBack} className="inline-flex items-center gap-[6px] bg-[#F3F5F9] mb-6 py-[10px] pl-[10px] pr-[16px] rounded-md lg:pl-0 lg:pr-0 lg:py-0 lg:bg-transparent lg:absolute lg:-top-[44px] lg:left-0" >
          <img src={`${rmVars.themeUrl}images/chevron_right_8D9AAF.png`} className="w-3 rotate-180" alt="back" />
          <span className="text-[14px] text-[#6C7687] font-medium leading-[100%] lg:text-[16px] xxl:text-red-500" >Back<span className="hidden lg:inline"> to Publication list</span>
          </span>
        </a>

        <PageBreadcrumb
          current={title}
          parent="Publication"
          parentTo="/publications"
          hideOnMobile
        />

        <div className="tit-area w-full flex justify-between items-center gap-[6px] mb-5 lg:mb-6" >
          <h2 className="mb-5 text-[22px] leading-[130%] font-black lg:text-[26px] lg:mb-6 lg:w-[calc(100%-100px)]">
            {(meta.hcp === 'y' || meta.hp === 'y') && (
              <>
                <span className="inline-flex flex-nowrap items-center gap-2 align-middle mb-1">
                  {meta.hcp === 'y' && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-[13px] font-bold leading-none bg-[#D4A017] shadow-sm shrink-0 mt-0 mb-0 align-middle">
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M19 4h2a1 1 0 0 1 1 1v2a5 5 0 0 1-5 5h-.35A6.02 6.02 0 0 1 13 14.7V16h3a1 1 0 1 1 0 2h-3v1h2a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h2v-1H6a1 1 0 1 1 0-2h3v-1.3A6.02 6.02 0 0 1 7.35 12H7a5 5 0 0 1-5-5V5a1 1 0 0 1 1-1h2V3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v1Zm-2 0H7v3a5 5 0 0 0 10 0V4ZM4 6v1a3 3 0 0 0 3 3h.2A6 6 0 0 1 6 6H4Zm16 0h-2a6 6 0 0 1-1.2 4H17a3 3 0 0 0 3-3V6ZM10 18v1h4v-1h-4Z" />
                      </svg>
                      HCP
                    </span>
                  )}
                  {meta.hp === 'y' && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-[13px] font-bold leading-none bg-[#F97316] shadow-sm shrink-0 mt-0 mb-0 align-middle">
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3.5 4 4 6.5-2.224 1.946-3.072 3.857-2 6 .5 1 1 1.62 1 3a2.5 2.5 0 0 1-2.5 2.5" />
                      </svg>
                      HP
                    </span>
                  )}
                </span>
                {' '}
              </>
            )}
            <span className="inline">{title}</span>
            {typeof rmVars !== 'undefined' && rmVars?.isEditor && id && (
              <a
                href={`${rmVars.homeUrl}/wp-admin/admin.php?page=resource&record_id=${id}`}
                onClick={(e) => e.stopPropagation()}
                // className="inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 ml-2"
                target="_blank"
                rel="noopener noreferrer"
                title="Edit Publication"
              >
                <span
                  className="editor-edit"
                  style={{
                    width: '18px',
                    height: '18px',
                    filter: 'invert(11%) sepia(70%) saturate(6625%) hue-rotate(348deg) brightness(88%) contrast(99%)',
                    backgroundSize: '18px auto',
                    display: 'inline-block',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                    backgroundImage: `url(${rmVars.themeUrl}images/edit-regular.svg)`
                  }}
                />
              </a>
            )}
          </h2>
          <button className="btn-print hidden px-3 py-[6px] items-center gap-[6px] bg-[#E4E8F1] rounded-[8px] cursor-pointer lg:flex" onClick={handlePrint} >
            <img src={`${rmVars.themeUrl}images/print-ico.png`} className="w-[24px] h-[24px]" alt="filter" />
            <span className="text-[14px] text-[#6C7687] font-semibold leading-[100%]" >Print</span >
          </button>
        </div>

        {record?.resource_title_translation && (
          <div className="pb-6 text-[14px] font-semibold text-[#8D9AAF] leading-[140%] lg:pb-[30px]" >
            {record.resource_title_translation}
          </div>
        )}

        <div className="py-6 flex flex-col gap-y-5 border-y border-y-[#E6E8EC] lg:pb-[32px]" >
          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium pt-1 lg:w-[110px]" >
              Document Type
            </p>
            <div className="flex flex-col gap-[6px] text-[13px] lg:flex-row lg:flex-wrap" >
              <span className="text-[#6C7687] bg-[#F3F5F9] px-[8px] py-[6px] rounded" >{record?.resource_type || ''}</span >
            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]" >
              Source
            </p>
            <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px]" >
              <a
                href={record?.journal_id ? `${rmVars.homeUrl}/journals/${record.journal_id}` : ''}
                className="basis-full w-full block mb-4 text-14px font-bold leading-[130%] lg:text-[14px] lg:leading-[150%] lg:mb-1 hover:underline underline-offset-2 decoration-1"
                target='_blank'
              >
                {record?.source || record?.journal_title || record?.conference || ''}
              </a>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-[6px] text-[14px] text-[#B4BCCC] leading-[100%]" >
                {record?.issn && (
                  <div>
                    <span>ISSN</span>
                    <span className="ml-[6px] font-semibold text-[#6C7687] lg:ml-[10px]" >{record?.issn}</span>
                  </div>
                )}

                {record?.eissn && (
                  <>
                    {(record?.issn) && <span className="hidden lg:inline-block w-[1px] h-[10px] bg-[#E4E8F1]"></span>}
                    <div>
                      <span>eISSN</span>
                      <span className="ml-[6px] font-semibold text-[#6C7687] lg:ml-[10px]" >{record?.eissn}</span>
                    </div>
                  </>
                )}

                {record?.publisher && (
                  <>
                    {(record?.issn || record?.eissn) && <span className="hidden lg:inline-block w-[1px] h-[10px] bg-[#E4E8F1]"></span>}
                    <div>
                      <span>Publisher</span>
                      <span className="ml-[6px] font-semibold text-[#6C7687] lg:ml-[10px]" >{record?.publisher}</span>
                    </div>
                  </>
                )}

                {(record?.volume || record?.issue) && (
                  <>
                    {(record?.issn || record?.eissn || record?.publisher) && <span className="hidden lg:inline-block w-[1px] h-[10px] bg-[#E4E8F1]"></span>}
                    <div>
                      <span>Volume/Issue</span>
                      <span className="ml-[6px] font-semibold text-[#6C7687] lg:ml-[10px]">
                        {[record?.volume || '', record?.issue ? `(${record?.issue})` : ''].filter(Boolean).join('')}
                      </span>
                    </div>
                  </>
                )}

                {pages && (
                  <>
                    {(record?.issn || record?.eissn || record?.publisher || record?.volume || record?.issue) && <span className="hidden lg:inline-block w-[1px] h-[10px] bg-[#E4E8F1]"></span>}
                    <div>
                      <span>Pages</span>
                      <span className="ml-[6px] font-semibold text-[#6C7687] lg:ml-[10px]">{`pp.${pages}`}</span>
                    </div>
                  </>
                )}

                {(record?.publish_date || record?.date) && (
                  <>
                    {(record?.issn || record?.eissn || record?.publisher || record?.volume || record?.issue || pages) && <span className="hidden lg:inline-block w-[1px] h-[10px] bg-[#E4E8F1]"></span>}
                    <div>
                      <span>Date</span>
                      <span className="ml-[6px] font-semibold text-[#6C7687] lg:ml-[10px]">{record?.publish_date || record?.date}</span>
                    </div>
                  </>
                )}
              </div>


            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]" >
              Publish Year
            </p>
            <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px]" >
              <span className="font-semibold">
                {record?.publish_year || ''}
                {record?.publish_date ? ` (${record.publish_date})` : ''}
              </span>
            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]" >
              Registered DB
            </p>
            <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px]" >
              {record?.registered_db && Array.isArray(record.registered_db) && record.registered_db.map((db, idx) => {
                const dbStr = String(db).toLowerCase();
                const type = dbStr.includes('kci') ? 'kci' : dbStr;
                return (
                  <span key={idx} className={`regist-tpye-${type} px-[5px] py-[2px] rounded`} >
                    {db}
                  </span>
                );
              })}
              {/*{record?.journal_resource_count > 0 && (*/}
              {/*  <a href={`?journal_id[]=${encodeURIComponent(record.journal_id)}`} className="ml-[6px] text-[13px] text-[#B4BCCC] underline underline-offset-2 font-medium lg:w-auto lg:ml-2" >*/}
              {/*    PNU Articles (<span>{record.journal_resource_count}</span>)*/}
              {/*  </a>*/}
              {/*)}*/}
            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium pt-1 lg:w-[110px]" >
              Journal Ranking
            </p>
            <div className="flex flex-col items-start gap-[6px] text-[13px] lg:flex-row lg:flex-wrap" >
              {record?.journal_ranking && record.journal_ranking.length > 0 ? (
                record.journal_ranking.map((db, idx) => (
                  <span key={idx} className={`type-${String(db.indicator_type || db.name).toLowerCase()} bg-[#F3F5F9] px-[8px] py-[6px] rounded`} >
                    {db.name} {db.year} <strong>Top {db.percent}% / Q{db.quartile}</strong>
                  </span>
                ))
              ) : (
                <span className="text-[#8D9AAF] pt-1">Not available</span>
              )}

              {record?.is_past_journal_indicator && (
                <span className="text-[#8D9AAF] px-[2px] py-[6px]">Not Finalized</span>
              )}
            </div>
          </div>

          <div className="flex items-start gap-y-[2px] gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]">
              Journal I/F
            </p>
            <div className="flex flex-wrap items-center gap-y-[2px] gap-x-[7px] text-[14px]">

              {record?.journal_impact && Array.isArray(record.journal_impact) && record.journal_impact.map((impact, idx) => {
                const labelMap = { jcr: 'JCR', kci: 'KCI', scopus: 'Scopus' };
                const typeLabel = labelMap[String(impact.indicator_type).toLowerCase()] || impact.indicator_type;
                const colorMap = { jcr: '#8D6DE2', scopus: '#F3652D', kci: '#398023' };
                const impactColor = colorMap[String(impact.indicator_type).toLowerCase()] || '#6C7687';

                return (
                  <div key={idx} className="flex items-center gap-2 px-2 py-1 text-[13px] text-[#6C7687] bg-[#F3F5F9] rounded-[4px]">
                    <span className="font-medium">{typeLabel} {impact.indicator_year}</span>
                    <span className="font-bold" style={{ color: impactColor }}>{impact.impact}</span>
                  </div>
                );
              })}

              {record?.is_past_journal_indicator && (
                <span className="text-[#8D9AAF]">Not Finalized</span>
              )}

              {record?.is_past_journal_indicator && record?.journal_ranking && record.journal_ranking.length > 0 && (
                <div className="flex flex-col items-start gap-[6px] text-[13px] lg:flex-row lg:flex-wrap" >
                  <span className="text-[#8D9AAF] pt-1">Notice: Showing last year's metrics due to pending update or exclusion.(학술지 인용지표가 발표되지 않았거나 제외되어 지난해의 지표를 보여줌)</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]" >
              Open Access
            </p>
            <div className="flex flex-wrap gap-[6px] text-[13px]">
              <span className="flex items-center gap-[3px] text-[#F68212] font-semibold" ><img src={`${rmVars.themeUrl}images/oa-gold-ico.png`} className="w-[11px]" alt="gold" />
                Gold OA(검토중)</span >
              <span className="flex items-center gap-[3px] text-[#398023] font-semibold" ><img src={`${rmVars.themeUrl}images/oa-green-ico.png`} className="w-[11px]" alt="green" />
                Green OA(검토중)</span >
            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]" >
              Collaboration
            </p>
            <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px]" >
              <span className="font-semibold">{collaborationLabel}</span>
            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p
              className="fwci-pop flex items-center gap-1 shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium cursor-pointer lg:w-[110px]"
              onClick={() => setFwciPopupOpen(true)}
            >
              FWCI
              <img src={`${rmVars.themeUrl}images/question-ico.png`} className="w-4" alt="info" />
            </p>
            <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px]">
              <span className="font-semibold">
                {record?.scopus_fwci ? `Scopus ${record.scopus_fwci}` : 'Not available'}
              </span>
            </div>

            <div
              className={`fwci-bkbg fixed top-0 left-0 w-full h-full bg-[rgba(0,0,0,0.4)] z-100 ${fwciPopupOpen ? '' : 'hidden'}`}
              onClick={() => setFwciPopupOpen(false)}
            ></div>

            <div className={`fwci-box fixed top-[122px] left-[50%] w-[calc(100%-80px)] p-10 translate-x-[-50%] z-100 bg-white rounded-[12px] lg:w-[600px] lg:top-[176px] ${fwciPopupOpen ? '' : 'hidden'}`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-[18px] font-bold">FWCI</h3>
                <button
                  className="fwci-close"
                  onClick={() => setFwciPopupOpen(false)}
                >
                  <img
                    src={`${rmVars.themeUrl}images/clear-icon-333.png`} className="w-[16px] cursor-pointer" alt="close" />
                </button>
              </div>

              <div className="text-[14px] leading-[140%] font-medium">
                <p className="mb-[10px]">
                  Field-Weighted Citation Impact (FWCI) in SciVal indicates
                  how the number of citations received by an entity’s
                  publications compares with the average number of citations
                  received by all other similar publications in the data
                  universe
                </p>

                <ul className="flex flex-col gap-[12px] list-disc pl-[20px]">
                  <li>
                    A FWCI of 1.00 indicates that the entity’s publications
                    have been cited exactly as would be expected based on
                    the global average for similar publications
                  </li>
                  <li>
                    A FWCI of more than 1.00 indicates above the global
                    average for similar publications; for example, 2.11
                    means 111% more than the world average.
                  </li>
                </ul>

                <p className="mb-[10px] mt-[16px]">
                SciVal의 FWCI(Field-Weighted Citation Impact)는 특정 기관(또는 연구자·학과 등)의 논문이 받은 인용 횟수가, 동일한 조건(연구분야, 출판연도, 문서 유형 등)이 유사한 전 세계 논문들의 평균 인용 횟수와 비교하여 어느 수준인지 나타내는 지표입니다.
                </p>

                <ul className="flex flex-col gap-[12px] list-disc pl-[20px]">
                  <li>
                  FWCI가 1.00이면, 해당 논문들의 인용 횟수가 유사한 논문들의 세계 평균 수준(기대값)과 동일함을 의미합니다.
                  </li>
                  <li>
                  FWCI가 1.00보다 크면, 유사한 논문들의 세계 평균보다 더 많이 인용되었음을 의미하며, 예를 들어 FWCI가 2.11인 경우 세계 평균 대비 111% 더 많이 인용되었음을 뜻합니다.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-x-2 lg:gap-x-3">
            <p className="shrink-0 w-[110px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]">
              Citation
            </p>
            <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[10px] text-[14px]">
              <div className="flex items-center gap-1">
                <img src={`${rmVars.themeUrl}images/citation-webofscience.png`} className="w-[18px]" alt="mark" />
                <span className="text-[13px] text-[#6C7687] font-semibold">Web of Science</span>
                <span className="text-[13px] font-bold">{record?.wos_cited_count || 0}</span>
              </div>

              <div className="flex items-center gap-1">
                <img src={`${rmVars.themeUrl}images/citation-scopus.png`} className="w-[18px]" alt="mark" />
                <span className="text-[13px] text-[#6C7687] font-semibold">Scopus</span>
                <span className="text-[13px] font-bold">{record?.scopus_cited_count || 0}</span>
              </div>

              <div className="flex items-center gap-1">
                <img
                  src={`${rmVars.themeUrl}images/citation-kci.png`} className="w-[18px]" alt="mark" />
                <span className="text-[13px] text-[#6C7687] font-semibold">KCI</span>
                <span className="text-[13px] font-bold">{record?.kci_cited_count || 0}</span>
              </div>

              {record?.cited_count_date && (
                <span className="text-[13px] text-[#8D9AAF] leading-[100%]">(Updated <span>{record.cited_count_date}</span>)</span>
              )}
            </div>
          </div>
        </div>

        <AuthorsTable authorView={authorView} setAuthorView={setAuthorView} filteredAuthors={filteredAuthors} />

        <div className="mt-[60px] lg:mt-[80px]">
          <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
            Journal Ranking Information by Category
          </h2>

          <ul className="flex flex-col gap-y-[8px] mb-4 p-6 pl-[34px] bg-[#F3F5F9] text-[14px] text-[#6C7687] font-medium rounded-xl list-disc lg:mb-6" >
            <li>
              If the ranking information or top percentile appears
              questionable, please contact the library for assistance.
            </li>
            <li>Information on JCR can be checked after logging in, while information for Scopus was referenced from <a href="https://www.elsevier.com/products/scopus/metrics/citescore" target="_blank" className="underline underline-offset-2">Here</a>, and KCI from <a href="https://www.kci.go.kr/" target="_blank" className="underline underline-offset-2">Here</a>.</li>

            {record?.is_past_journal_indicator && (
              <li>Showing <span className="font-semibold">last year's metrics</span> due to pending update or exclusion.(학술지 인용지표가 발표되지 않았거나 제외되어 <span className="font-semibold">지난해의 지표</span>를 보여줌)</li>
            )}
          </ul>

          {(() => {
            const categoryRanking = arr(record?.category_ranking);
            if (!categoryRanking || categoryRanking.length === 0) return null;
            const orderMap = { jcr: 0, scopus: 1, kci: 2 };
            const types = Array.from(new Set(categoryRanking.map(r => String(r?.indicator_type || '').toLowerCase()).filter(Boolean)));
            types.sort((a, b) => (orderMap[a] ?? 99) - (orderMap[b] ?? 99));
            if (types.length <= 0) return null;

            return (
              <ul id="customOptions" className="mb-4 p-[6px] bg-[#F3F5F9] rounded-lg z-10 text-[#8D9AAF] inline-flex text-[15px] gap-[5px]">
                {types.map(type => (
                  <li
                    key={type}
                    className={(activeTab === type ? 'active ' : '') + 'w-[150px] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer'}
                    onClick={() => setActiveTab(type)}
                    data-value={type}
                  >
                    {type.toUpperCase()}

                    {record?.is_past_journal_indicator && (
                      <span className="text-[12px]">(Not Finalized)</span>
                    )}
                  </li>
                ))}
              </ul>
            );
          })()}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-fixed">
              <colgroup>
                <col className="w-[50px] !p-[10px]" />
                <col className="w-[280px] lg:w-[200px]" />
                <col className="w-[120px]" />
                <col className="w-[80px]" />
                <col className="w-[80px]" />
                <col className="w-[80px]" />
                <col className="w-[60px]" />
                <col className="w-[50px]" />
              </colgroup>

              <thead>
                <tr>
                  <th className="!p-[10px]" style={{ paddingLeft: '4px', paddingRight: '4px' }}>No.</th>
                  <th style={{ paddingLeft: '4px', paddingRight: '4px' }}>Category</th>
                  <th style={{ paddingLeft: '4px', paddingRight: '4px' }}>Num of Journal</th>
                  <th style={{ paddingLeft: '4px', paddingRight: '4px' }}>Ranking</th>
                  <th style={{ paddingLeft: '4px', paddingRight: '4px' }}>Percent</th>
                  <th style={{ paddingLeft: '4px', paddingRight: '4px' }}>Percentile</th>
                  <th style={{ paddingLeft: '4px', paddingRight: '4px' }}>Quartile</th>
                  <th style={{ paddingLeft: '4px', paddingRight: '4px' }}>Year</th>
                </tr>
              </thead>

              <tbody>
                {(() => {
                  const categoryRanking = arr(record?.category_ranking);
                  if (!categoryRanking || categoryRanking.length === 0) {
                    return (
                      <tr>
                        <td colSpan="8" className="!p-[10px] text-center text-[14px] text-[#6C7687]" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                          No category ranking data.
                        </td>
                      </tr>
                    );
                  }

                  // 정렬: 연도 내림차순, 타입 순서 (JCR, Scopus, KCI), 랭킹 오름차순
                  const orderMap = { jcr: 0, scopus: 1, kci: 2 };
                  const filtered = categoryRanking.filter(r => {
                    if (!r || !r.category_name) return false;
                    if (!activeTab) return true;
                    return String(r.indicator_type || '').toLowerCase() === activeTab;
                  });

                  if (filtered.length === 0) {
                    return (
                      <tr>
                        <td colSpan="9" className="!p-[10px] text-center text-[14px] text-[#6C7687]" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                          No category ranking data for this DB.
                        </td>
                      </tr>
                    );
                  }

                  const sorted = [...filtered]
                    .sort((a, b) => {
                      const ya = Number(a?.indicator_year || 0);
                      const yb = Number(b?.indicator_year || 0);
                      if (yb !== ya) return yb - ya; // year desc
                      const ta = orderMap[String(a?.indicator_type || '').toLowerCase()] ?? 99;
                      const tb = orderMap[String(b?.indicator_type || '').toLowerCase()] ?? 99;
                      if (ta !== tb) return ta - tb; // type order
                      const ra = Number(a?.ranking || 0);
                      const rb = Number(b?.ranking || 0);
                      if (ra !== rb) return ra - rb; // better rank first
                      return String(a?.category_name || '').localeCompare(String(b?.category_name || ''));
                    });

                  return sorted.map((item, idx) => {
                    const isJcr = String(item?.indicator_type || '').toLowerCase() === 'jcr';
                    const indicatorType = item?.indicator_type ? String(item.indicator_type).toUpperCase() : '';
                    const edition = item?.edition ? String(item.edition).toUpperCase() : '';
                    const categoryName = item?.category_name || '';
                    const categoryLabel = isJcr && edition ? `${edition} - ${categoryName}` : categoryName;
                    const year = item?.indicator_year || '';
                    const percentile = item?.percentile ?? '';
                    const quartile = item?.quartile ?? '';

                    // JCR 2023 이상의 경우 대체 값 표시
                    const showAlt = isJcr && year >= 2023;
                    const categoryCount = item?.category_count ?? '';
                    const categoryCount2 = item?.category_count2;
                    const ranking = item?.ranking ?? '';
                    const ranking2 = item?.ranking2;
                    const percent = item?.percent !== null && item?.percent !== undefined ? `${Number(item.percent).toFixed(2)}%` : '';
                    const percent2 = item?.percent2 !== null && item?.percent2 !== undefined ? `${Number(item.percent2).toFixed(2)}%` : '';

                    return (
                      <tr key={idx}>
                        <td className="!p-[10px]" style={{ paddingLeft: '4px', paddingRight: '4px' }}>{idx + 1}</td>
                        <td className="!whitespace-normal" style={{ paddingLeft: '4px', paddingRight: '4px' }}>{categoryLabel}</td>
                        <td style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                          {categoryCount || 'N/A'}
                          {showAlt && categoryCount2 != null && (
                            <>
                              <br /><span>({categoryCount2})*</span>
                            </>
                          )}
                        </td>
                        <td style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                          {ranking || 'N/A'}
                          {showAlt && ranking2 != null && (
                            <>
                              <br /><span>({ranking2})*</span>
                            </>
                          )}
                        </td>
                        <td style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                          {percent || 'N/A'}
                          {showAlt && percent2 && (
                            <>
                              <br /><span>({percent2})*</span>
                            </>
                          )}
                        </td>
                        <td style={{ paddingLeft: '4px', paddingRight: '4px' }}>{percentile || 'N/A'}</td>
                        <td style={{ paddingLeft: '4px', paddingRight: '4px' }}>{quartile || 'N/A'}</td>
                        <td style={{ paddingLeft: '4px', paddingRight: '4px' }}>{year || 'N/A'}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {abstract && (
          <div className="mt-[50px]">
            <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
              Abstract
            </h2>
            <div className="text-[14px] text-[#6C7687] leading-[140%] lg:text-[15px] lg:leading-[150%]" >
              {abstract}
            </div>

            {Array.isArray(keywords) && keywords.length > 0 && (
              <div className="p-[18px] mt-5 border border-[#E4E8F1] rounded-[12px] lg:mt-4" >
                <p className="mb-3 text-[14px] font-semibold leading-[100%] lg:mb-4" >
                  Keywords
                </p>
                <div className="flex flex-wrap items-center gap-[6px] text-[13px] text-[#6C7687] leading-[100%]] lg:text-[15px]" >
                  {keywords.map((kw, idx) => (
                    <Link key={idx} to={`/publications?keyword=${encodeURIComponent(kw)}`} className="px-3 py-[6px] bg-[#F3F5F9] rounded-full hover:bg-[#E4E8F1] transition-colors" >{kw}</Link >
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!abstract && Array.isArray(keywords) && keywords.length > 0 && (
          <div className="mt-[50px]">
            <div className="p-[18px] mt-5 border border-[#E4E8F1] rounded-[12px] lg:mt-4" >
              <p className="mb-3 text-[14px] font-semibold leading-[100%] lg:mb-4" >
                Keywords
              </p>
              <div className="flex flex-wrap items-center gap-[6px] text-[13px] text-[#6C7687] leading-[100%]] lg:text-[15px]" >
                {keywords.map((kw, idx) => (
                  <Link key={idx} to={`/publications?keyword=${encodeURIComponent(kw)}`} className="px-3 py-[6px] bg-[#F3F5F9] rounded-full hover:bg-[#E4E8F1] transition-colors" >{kw}</Link >
                ))}
              </div>
            </div>
          </div>
        )}


        <ReferencesTable resourceId={id} />

        <AuthorOtherPapers resourceId={id} scholarIds={scholarIds} perPage={3} />

        {(record?.eid || record?.scopus_id) && (
          <div className="hidden mt-[60px] lg:block lg:mt-[80px]">
            <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
              Citing papers
            </h2>

            <CitingPapersList
              query={record?.eid ? `REF(${record.eid})` : (record?.scopus_id ? `REF(${record.scopus_id})` : null)}
            />
          </div>
        )}
      </div>

      <div className="right-box hidden mt-[105px] w-[310px] lg:block">
        <div className="w-full p-8 bg-white rounded-xl">
          <h2 className="flex items-center gap-[6px] mb-5">
            <img src={`${rmVars.themeUrl}images/link-ico.png`} className="w-[22px]" alt="link icon" />
            <span className="text-[18px] font-bold">Extra Link</span>
          </h2>

          <div className="flex flex-col gap-1">
            {record?.wos_id && (
              <a href={`https://www.webofscience.com/wos/woscc/full-record/WOS:${record?.wos_id}`} className="flex justify-between items-center px-[14px] py-[10px] bg-[#F3F5F9] rounded-md" target="_blank">
                <span className="text-[#376BDB] text-[14px] font-semibold" >Web of Science</span >
                <img src={`${rmVars.themeUrl}images/launch-ico-bl.png`} className="w-[18px]" alt="launch" />
              </a>
            )}

            {record?.scopus_id && (
              <a href={`https://www.scopus.com/record/display.uri?eid=${record?.scopus_id}&origin=inward`} className="flex justify-between items-center px-[14px] py-[10px] bg-[#F3F5F9] rounded-md" target="_blank">
                <span className="text-[#376BDB] text-[14px] font-semibold" >Scopus</span >
                <img src={`${rmVars.themeUrl}images/launch-ico-bl.png`} className="w-[18px]" alt="launch" />
              </a>
            )}

            {record?.kci_id && (
              <a href={`https://www.kci.go.kr/kciportal/ci/sereArticleSearch/ciSereArtiView.kci?sereArticleSearchBean.artiId=${record?.kci_id}&origin=inward`} className="flex justify-between items-center px-[14px] py-[10px] bg-[#F3F5F9] rounded-md" target="_blank">
                <span className="text-[#376BDB] text-[14px] font-semibold" >KCI</span >
                <img src={`${rmVars.themeUrl}images/launch-ico-bl.png`} className="w-[18px]" alt="launch" />
              </a>
            )}

            {record?.doi && (
              <a href={`https://doi.org/${record?.doi}`} target="_blank" rel="noreferrer" className="flex justify-between items-center px-[14px] py-[10px] bg-[#F3F5F9] rounded-md" >
                <span className="text-[#376BDB] text-[14px] font-semibold">DOI article</span>
                <img src={`${rmVars.themeUrl}images/launch-ico-bl.png`} className="w-[18px]" alt="launch" />
              </a>
            )}

            {(record?.url || record?.link) && (
              <a href={record?.url || record?.link} target="_blank" rel="noreferrer" className="flex justify-between items-center px-[14px] py-[10px] bg-[#F3F5F9] rounded-md" >
                <span className="text-[#376BDB] text-[14px] font-semibold">Fulltext</span>
                <img src={`${rmVars.themeUrl}images/launch-ico-bl.png`} className="w-[18px]" alt="launch" />
              </a>
            )}

            {(record?.url || record?.link) && (
              <a href={record?.url || record?.link} target="_blank" rel="noreferrer" className="flex justify-between items-center px-[14px] py-[10px] bg-[#F3F5F9] rounded-md" >
                <span className="text-[#376BDB] text-[14px] font-semibold">e-Journal</span>
                <img src={`${rmVars.themeUrl}images/launch-ico-bl.png`} className="w-[18px]" alt="launch" />
              </a>
            )}
          </div>
        </div>

        {record?.doi && (
          <div className="w-full p-8 mt-8 bg-white rounded-xl">
            <h2 className="flex items-center gap-[6px] mb-5">
              <span className="text-[18px] font-bold">Altmetric</span>
            </h2>

            <div className="flex items-center justify-center gap-6 w-full">
              <div
                ref={altmetricRef}
                className="altmetric-embed flex items-center justify-center"
                data-doi={record.doi}
                data-badge-type="donut"
                data-badge-width="60"
                data-badge-popover="right"
                data-hide-no-mentions="true"
              />
              <a
                href={`https://plu.mx/plum/a/?doi=${record.doi}`}
                className="plumx-plum-print-popup"
                data-size="large"
                data-popup="left"
                data-hide-when-empty="true"
              >
              </a>
            </div>
          </div>
        )}

        <div className="w-full p-8 mt-8 bg-white rounded-xl">
          <h2 className="flex items-center gap-[6px] mb-5">
            <span className="text-[18px] font-bold" >Number of Authors by Country</span >
          </h2>

          <AuthorsByCountryDonut resourceId={id} />
        </div>

        <div className="w-full p-8 mt-8 bg-white rounded-xl">
          <h2 className="flex items-center gap-[6px] mb-5">
            <span className="text-[18px] font-bold" >Number of Authors by Institution</span >
          </h2>

          <AuthorsByInstitutionDonut resourceId={id} />
        </div>
      </div>
    </div>
  );
}
